import type { ChangeEvent } from 'react';
import { platformLabel } from '@discovr/types';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { VideoCamera, MonitorPlay, CurrencyInr, Info } from '../design-system/icons';
import type { Control, UseFormRegister, UseFormSetValue, FieldErrors } from 'react-hook-form';
import type { CampaignFormData } from './campaignSchema';
import { err, invalidBorder } from './constants';
import { formatNumber, unformatNumber } from './helpers';
import { DeliverablesSection } from './DeliverablesSection';

interface StepBudgetProps {
  register: UseFormRegister<CampaignFormData>;
  setValue: UseFormSetValue<CampaignFormData>;
  control: Control<CampaignFormData>;
  errors: FieldErrors<CampaignFormData>;
  platform: CampaignFormData['platform'];
  deliverables: string[];
  deliverables_text: string;
  usageRights: CampaignFormData['usageRights'];
  totalBudget: number | undefined;
  payoutModel: CampaignFormData['payoutModel'];
}

const PAYOUTS = [
  { val: 'fixed_fee', label: 'Fixed Fee' },
  { val: 'revenue_share', label: 'Revenue Share' },
  { val: 'gifting_only', label: 'Gifting Only' },
] as const;

// Figma: each step is stacked cards — a header card, then one card per section.
const CARD = 'rounded-xl border border-border bg-card px-8 py-8';

export function StepBudget({
  register, setValue, control, errors, platform,
  deliverables, deliverables_text, usageRights, totalBudget, payoutModel,
}: StepBudgetProps) {
  const charCount = (deliverables_text ?? '').length;

  // Budget is stored as a plain number but shown comma-formatted. Empty
  // clears to undefined ("never typed a budget"), not 0 ("explicitly typed
  // zero") — the campaign details page shows "₹0" only for the latter and
  // hides the budget line for the former. For fixed_fee, the existing
  // payoutModel/totalBudget cross-field refine in campaignSchema.ts already
  // rejects a missing/undefined budget with a clear "at least ₹1" message.
  const handleBudgetChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = unformatNumber(e.target.value);
    // shouldValidate: true — this field is controlled (not `register`ed), so
    // RHF's normal reValidateMode never fires for it. Without this, fixing a
    // fixed_fee "at least ₹1" error by typing a valid amount left the old
    // error message on screen until the user clicked Continue again.
    setValue('totalBudget', raw === '' ? undefined : Number(raw), { shouldValidate: true });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header card */}
      <div className={`${CARD} flex items-start gap-3.5`}>
        <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-muted">
          <VideoCamera size={20} className="text-foreground" />
        </span>
        <div>
          <h2 className="m-0 text-heading-h5 text-foreground">Deliverables &amp; budget</h2>
          <p className="m-0 text-body-sm-regular text-muted-foreground">Pick what creators produce, then set the spend.</p>
        </div>
      </div>

      {/* Deliverables card: content-type cards (required) + optional free text */}
      <div className={`${CARD} flex flex-col gap-6`}>
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-2 text-body-md-medium text-foreground">
            <MonitorPlay size={14} className="text-muted-foreground" />
            {`${platformLabel(platform, 'YouTube')} content types`}
            <span className="text-[14px] text-foreground-destructive-default">*</span>
          </span>
          <span className="text-caption-regular text-muted-foreground">
            What each creator will post. Pick one or more.
          </span>
        </div>
        <div className="mt-1">
          <DeliverablesSection
            control={control}
            setValue={setValue}
            errors={errors}
            platform={platform}
            watchDeliverables={deliverables}
            watchUsageRights={usageRights}
          />
        </div>

        <hr className="border-border" />

        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-2 text-body-md-medium text-foreground">
            Deliverables description
            <span className="text-caption-regular font-normal text-muted-foreground/70">optional</span>
          </span>
          <span className="text-caption-regular text-muted-foreground">
            Anything else creators should know — counts, formats, approval requirements.
          </span>
        </div>
        <Textarea
          className={`min-h-[96px] resize-y leading-relaxed ${invalidBorder(!!errors.deliverables_text)}`}
          rows={3}
          maxLength={20000}
          aria-invalid={!!errors.deliverables_text}
          placeholder="e.g. 1 dedicated video + 3 integrations. Approval required before posting."
          {...register('deliverables_text')}
        />
        <div className="flex items-center justify-between">
          {errors.deliverables_text && <span className={err}>{errors.deliverables_text.message}</span>}
          <span className={`ml-auto text-caption-regular ${charCount > 19000 ? 'text-amber-500' : 'text-muted-foreground'}`}>
            {charCount.toLocaleString()} / 20,000
          </span>
        </div>
      </div>

      {/* Budget & payout card (Figma: Total budget | Payout model in a row) */}
      <div className={`${CARD} flex flex-col gap-5`}>
        <div className="flex flex-col gap-0.5 pb-4">
          <span className="flex items-center gap-2 text-body-md-medium text-foreground">
            <CurrencyInr size={14} className="text-muted-foreground" /> Budget &amp; payout
            <span className="text-[14px] text-foreground-destructive-default">*</span>
          </span>
          <span className="text-caption-regular text-muted-foreground">Total spend and how creators get paid.</span>
        </div>

        <div className="flex flex-wrap gap-x-5 gap-y-4">
          {/* Total budget */}
          <div className="flex flex-col gap-1.5">
            <span className="flex items-center gap-1 text-caption-medium text-foreground-secondary-default">
              Total budget
              {payoutModel === 'fixed_fee' && <span className="text-foreground-destructive-default">*</span>}
            </span>
            <div className="relative w-[220px]">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground">₹</span>
              <Input
                className={`h-10 pl-7 ${invalidBorder(!!errors.totalBudget)}`}
                type="text"
                inputMode="numeric"
                placeholder="10,000"
                // 25 comfortably covers the comma-formatted CAMPAIGN_BUDGET_MAX
                // (21 chars) with headroom — a defensive cap against a runaway
                // digit string pushing the parsed number toward Infinity/
                // precision loss before the schema's own .max() error can
                // fire. Not exact-to-the-digit on purpose: the schema's
                // CAMPAIGN_BUDGET_MAX check is the authoritative, precisely
                // correct bound with a clear message.
                maxLength={25}
                aria-invalid={!!errors.totalBudget}
                value={totalBudget !== undefined ? formatNumber(totalBudget) : ''}
                onChange={handleBudgetChange}
              />
            </div>
            {errors.totalBudget ? (
              <span className={err}>{errors.totalBudget.message}</span>
            ) : (
              payoutModel !== 'fixed_fee' && (
                <span className="text-caption-regular text-muted-foreground">
                  Optional for {PAYOUTS.find((p) => p.val === payoutModel)?.label} campaigns
                </span>
              )
            )}
          </div>

          {/* Payout model — pills (Figma) */}
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <span className="flex items-center gap-1.5 text-caption-medium text-foreground-secondary-default">
              Creator payout model<span className="text-foreground-destructive-default">*</span>
              <Info size={14} className="text-muted-foreground" />
            </span>
            <div className="flex flex-wrap gap-2.5">
              {PAYOUTS.map((opt) => {
                const active = payoutModel === opt.val;
                return (
                  <button
                    key={opt.val}
                    type="button"
                    onClick={() => setValue('payoutModel', opt.val, { shouldValidate: true })}
                    className={`h-10 cursor-pointer rounded-lg border px-6 text-body-sm-medium transition-colors ${active ? 'border-foreground bg-foreground text-background' : 'border-border bg-card text-foreground hover:border-foreground/30'}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {errors.payoutModel && <span className={err}>{errors.payoutModel.message}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
